import Analytics from '@serv/Analytics'
import Auth from '@serv/Auth'
import Auth0 from '@serv/Auth/Auth0'
import ChatService from '@serv/ChatService'
import Config from '@serv/Config'
import ConfigManager from '@config/ConfigManager'
import { hasSentryReplays } from '@/utils/userUtils'
import ListService from '@serv/ListService'
import Locale from '@serv/Locale'
import Logging from '@serv/Logging'
import NotifyService from '@serv/NotifyService'
import Redirect from '@serv/Redirect'
import Request from '@serv/Request'
import { resetAllStores } from '@/plugins/piniaResetStore'
import { setUser } from '@sentry/vue'
import Storage from '@serv/Storage'
import store from '@/store'
import UnauthenticatedAuth from '@serv/Auth/UnauthenticatedAuth'
import Whoami from '@serv/Whoami'

/**
 * Helpers for managing the user login and logout process..
 */
class LoginService {
    // Log the user out, but do not navigate anywhere.
    logout(config) {
        Logging.log('Logging out...')

        // Logout of 3rd party services
        Analytics.logOut()
        ChatService.disconnect()
        setUser()
        const user = store.state.user.user
        if (hasSentryReplays(user)) {
            import('@/utils/sentryReplay.js').then(({ stopReplay }) => {
                stopReplay()
            })
        }

        this.#setLogoutNotification(config)

        Request.removeErrorCallback(401)
        Whoami.invalidate()
        const region = Config.country
        Config.reset() // e.g. clear 'country' i.e. region

        // Remove everything from local storage
        Storage.clear()
        UnauthenticatedAuth.reset()

        // Reset entire Vuex state
        store.commit('reset')

        // Reset Pinia stores
        resetAllStores()

        // Reset other globals
        ListService.reset()

        return new Promise((resolve, reject) => {
            if (Storage.get('auth_source') == 'auth0' || config?.isAuth0) {
                Auth0.reset()
                Auth0.revokeToken(region || config?.region)
                reject()
            } else {
                Auth.reset()
                Auth.revokeToken()
                resolve()
            }
        })
    }

    #setLogoutNotification(config) {
        const currentRoute = Redirect.router.currentRoute.value
        let messageText, messageFunction
        if (ConfigManager.config.client !== 'corin' && !config?.ignoreNotification) {
            if (currentRoute.params && currentRoute.notAuthorised) {
                messageText = Locale.getLanguageItem('loginUnauthorisedFromFrontEnd')
                messageFunction = 'error'
            } else if (currentRoute.query.expired) {
                messageText = Locale.getLanguageItem('accountLogOutSessionExpired')
                messageFunction = 'error'
            } else {
                messageText = Locale.getLanguageItem('accountLogOutOccurred')
                messageFunction = 'info'
            }
        }

        if (messageText && messageFunction) {
            Storage.set('logoutNotification', {
                messageText,
                messageFunction
            })
        }
    }
}

export default new LoginService()
